import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Rep } from '../../classes/Workout';
import { SimpleWorkoutManager } from '../../utils/SimpleWorkoutManager';
import './VisualTimerBar.css';

interface VisualTimerBarProps {
  dispatch: Dispatch<any>;
  activityStatus: string;
  timerStatus: string;
  currentRep: Rep;
  elapsedTime: number;    // Global elapsed time (for get ready countdown)
  repElapsedTime: number; // Per-rep elapsed time (for individual rep progress)
}


interface VisualTimerBarState {
  // No longer need internal timer state - using prop instead
}

class VisualTimerBar extends Component<VisualTimerBarProps, VisualTimerBarState> {
  private workoutManager: SimpleWorkoutManager;

  constructor(props: VisualTimerBarProps) {
    super(props);
    this.workoutManager = SimpleWorkoutManager.getInstance();
    this.state = {};
  }

  // No longer need timer lifecycle methods - using elapsedTime prop instead

  render() {
    const { currentRep, activityStatus, elapsedTime, repElapsedTime } = this.props;
    
    // Calculate progress percentage (0-100) with smooth progression
    const progressPercentage = (() => {
      // For get ready phase, show countdown progress (3 seconds) using global timer
      if (activityStatus === 'get ready') {
        const getReadyTime = 3.0; // 3 second countdown
        return Math.min((elapsedTime / getReadyTime) * 100, 100);
      }
      
      // For ready and wait start states, show 0% progress
      if (activityStatus === 'ready' || activityStatus === 'wait start') {
        return 0;
      }
      
      // wait_hang: rest just ended, hold the bar at 100% (grey) until user pulls
      if (activityStatus === 'wait hang') return 100;

      // wait_release: hang timer done, hold at 100% while user releases
      if (activityStatus === 'wait release') return 100;

      // For active reps, show progress based on per-rep elapsed time.
      // elapsedTime resets to 0 at the start of each rep (via timerStatus='reset' in Timer.tsx).
      if (currentRep && currentRep.time > 0 && elapsedTime > 0) {
        const progress = Math.min((elapsedTime / currentRep.time) * 100, 100);
        return Math.max(0, progress);
      }
      
      return 0;
    })();

    // Determine if this is a hang or rest based on rep intensity and activity status.
    // 'wait hang' stays grey (rest colour) — the user hasn't pulled yet.
    // 'wait release' stays red (hang colour) — the hold just finished but user is still on.
    const isHang = activityStatus === 'hang' || activityStatus === 'wait release';
    const isRest = activityStatus === 'rest'
      || activityStatus === 'wait rest'
      || activityStatus === 'wait hang'
      || (currentRep && currentRep.intensity === 0 && !isHang);

    // Get the appropriate color based on rep type
    const getProgressColor = () => {
      if (activityStatus === 'get ready') return '#F39C12'; // Orange for get ready countdown
      if (activityStatus === 'ready' || activityStatus === 'wait start') return 'var(--color-primary)';
      if (isHang) return 'var(--color-primary)';
      if (isRest) return '#878C91'; // Light grey for rests
      return 'var(--color-primary)';
    };

    // Get rep type label from workout manager
    const getRepTypeLabel = () => {
      if (!this.workoutManager.isInitialized()) {
        return 'READY';
      }
      return this.workoutManager.getRepType();
    };

    // Format countdown time display (remaining time)
    const formatCountdownTime = (): string => {
      // For get ready phase, show countdown (3 seconds)
      if (activityStatus === 'get ready') {
        const getReadyTime = 3.0; // 3 second countdown
        const remainingTime = Math.max(0, getReadyTime - elapsedTime);
        const seconds = Math.floor(remainingTime);
        const tenths = Math.floor((remainingTime % 1) * 10);
        
        return `00:${seconds.toString().padStart(2, '0')}.${tenths}`;
      }
      
      // For ready state, show "00:00.0" (no countdown)
      if (activityStatus === 'ready') {
        return '00:00.0';
      }
      
      // For wait start state, show "00:00.0" (no countdown)
      if (activityStatus === 'wait start') {
        return '00:00.0';
      }

      // wait_hang: rest done, no active timer — show zero
      if (activityStatus === 'wait hang') {
        return '00:00.0';
      }

      // wait_release: hang timer done, no active timer — show zero
      if (activityStatus === 'wait release') {
        return '00:00.0';
      }

      // For active reps, show remaining time using per-rep elapsed time.
      // elapsedTime is reset to 0 on timerStatus='reset' so it counts from the start of each rep.
      if (!currentRep || currentRep.time <= 0) return '00:00.0';

      const remainingTime = Math.max(0, currentRep.time - elapsedTime);
      const minutes = Math.floor(remainingTime / 60);
      const seconds = Math.floor(remainingTime % 60);
      const tenths = Math.floor((remainingTime % 1) * 10);

      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${tenths}`;
    };

    // Get status indicator (currently unused but kept for future use)
    // const getStatusIndicator = () => {
    //   switch (timerStatus) {
    //     case 'working': return '▶';
    //     case 'pause': return '⏸';
    //     case 'done': return '✓';
    //     default: return '●';
    //   }
    // };

    // Get status text (currently unused but kept for future use)
    // const getStatusText = () => {
    //   switch (timerStatus) {
    //     case 'working': return 'Working';
    //     case 'pause': return 'Paused';
    //     case 'done': return 'Done';
    //     default: return 'Ready';
    //   }
    // };

    return (
      <div className="visual-timer-bar">
        {/* Progress bar background */}
        <div className="timer-bar-background">
          {/* Progress indicator */}
          <div 
            className="timer-bar-progress"
            style={{
              width: `${progressPercentage}%`,
              backgroundColor: getProgressColor()
            }}
          />
        </div>

        {/* Content overlay */}
        <div className="timer-bar-content">
          {/* Left side - Rep type only */}
          <div className="timer-bar-left">
            <div className="rep-type-label">
              {getRepTypeLabel()}
            </div>
          </div>

          {/* Right side - Countdown timer */}
          <div className="timer-bar-right">
            <div className="countdown-timer">
              {formatCountdownTime()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  activityStatus: state.activities.activityStatus,
  timerStatus: state.activities.timerStatus,
  currentRep: state.activities.currentRep,
  elapsedTime: state.activities.elapsedTime,         // Global elapsed time (get ready countdown)
  repElapsedTime: state.activities.repTimer?.elapsedTime || 0, // Per-rep elapsed time (rep countdown)
});

export default connect(mapStateToProps)(VisualTimerBar);
